import React, { useCallback, useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Slice, Fragment } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Blockquote from '@tiptap/extension-blockquote';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { ResizableImage } from './tiptap_image';
import { showToast } from '../lib/toast';
import { ValueRow, ValueRowLabel, ValueRowValue } from './tiptap_value_row';
import { Indent } from './tiptap_indent';
import { PageButton } from './tiptap_page_button';
import { Ico } from './icons';
import { uploadImage } from '../lib/api';

export const PageButtonContext = React.createContext({
  doc: null,
  onOpenPage: null,
  onInsertInlineTab: null,
  onRenameInlineTab: null,
  onUpdateInlineTab: null,
  onDeleteInlineTab: null,
  onClonePageButtonSubtree: null,
});

const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: el => el.style.fontSize || null,
        renderHTML: attrs => {
          if (!attrs.fontSize) return {};
          return { style: `font-size: ${attrs.fontSize}` };
        },
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setFontSize: (size) => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: size }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});

const FONT_SIZES = [
  { label: 'XS', value: 'var(--t-12)' },
  { label: 'S',  value: 'var(--t-14)' },
  { label: 'M',  value: 'var(--t-16)' },
  { label: 'L',  value: 'var(--t-18)' },
  { label: 'XL', value: 'var(--t-24)' },
];

const CalloutBlockquote = Blockquote.extend({
  addAttributes() {
    return {
      tone: {
        default: 'note',
        parseHTML: el => el.getAttribute('data-tone') || 'note',
        renderHTML: attrs => ({ 'data-tone': attrs.tone }),
      },
    };
  },
});

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

// Snapshots of inline subtrees that were cut, so the next paste can restore
// them as a move (same tabId, content intact) instead of cloning. Cut removes
// the tabs/pages from doc immediately so the page visibly disappears; the
// snapshot lives here until a paste consumes it or the TTL expires.
// Maps rootTabId → { tabs, pages, expiry }. Module-level so it's shared
// across the multiple TiptapEditor instances that render for one document.
const cutBuffer = new Map();
const CUT_BUFFER_TTL_MS = 5 * 60 * 1000;

function snapshotInlineSubtree(doc, rootTabId) {
  if (!doc) return null;
  const visited = new Set();
  const queue = [rootTabId];
  const tabs = [];
  const pages = {};
  while (queue.length) {
    const tid = queue.shift();
    if (visited.has(tid)) continue;
    visited.add(tid);
    const tab = doc.tabs.find(t => t.id === tid);
    if (!tab) continue;
    tabs.push(tab);
    tab.pageIds.forEach(pid => {
      const pg = doc.pages[pid];
      if (!pg) return;
      pages[pid] = pg;
      const walk = (n) => {
        if (!n) return;
        if (n.type === 'pageButton' && n.attrs?.tabId) {
          const child = doc.tabs.find(t => t.id === n.attrs.tabId);
          if (child?.inline) queue.push(child.id);
        }
        (n.content || []).forEach(walk);
      };
      if (pg.content) walk(pg.content);
      if (pg.content2) walk(pg.content2);
    });
  }
  // Deep-clone so the snapshot is insulated from any in-place mutation of
  // the live React doc during the TTL window.
  return structuredClone({ tabs, pages });
}

let uploadId = 0;

function uploadAndInsert(editor, file, pos) {
  if (!editor) return;
  const id = `upload-${++uploadId}`;
  const insertAttrs = { src: null, uploading: true, alt: id };

  if (pos != null) {
    const node = editor.state.schema.nodes.resizableImage.create(insertAttrs);
    editor.view.dispatch(editor.state.tr.insert(pos, node));
  } else {
    editor.chain().focus().setImage(insertAttrs).run();
  }

  uploadImage(file)
    .then(({ url, imageId }) => {
      editor.state.doc.descendants((node, nodePos) => {
        if (node.type.name === 'resizableImage' && node.attrs.alt === id) {
          editor.view.dispatch(
            editor.state.tr.setNodeMarkup(nodePos, undefined, {
              ...node.attrs, src: url, imageId, uploading: false, alt: '',
            })
          );
        }
      });
    })
    .catch(err => {
      console.error('Image upload failed:', err);
      showToast(err.message || 'Image upload failed', { type: 'error' });
      editor.state.doc.descendants((node, nodePos) => {
        if (node.type.name === 'resizableImage' && node.attrs.alt === id) {
          editor.view.dispatch(editor.state.tr.delete(nodePos, nodePos + node.nodeSize));
        }
      });
    });
}

function insertImage(editor) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/jpeg,image/png,image/gif,image/webp';
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    uploadAndInsert(editor, file);
  };
  input.click();
}

const CALLOUTS = [
  { tone: 'note',    label: 'Note',    cls: 'tt-co--note',
    icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/><path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="8" cy="5" r="0.8" fill="currentColor"/></svg> },
  { tone: 'info',    label: 'Caution', cls: 'tt-co--caution',
    icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M8 7v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="8" cy="11.5" r="0.8" fill="currentColor"/></svg> },
  { tone: 'warn',    label: 'Warning', cls: 'tt-co--warn',
    icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2L1.5 13h13L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M8 6v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="8" cy="11.5" r="0.8" fill="currentColor"/></svg> },
  { tone: 'success', label: 'Success', cls: 'tt-co--success',
    icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/><path d="M5.5 8l2 2 3.5-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
];

export function TiptapToolbar({ editor, zoom, onSetZoom, preview, onUndo, onRedo, canUndo, canRedo, responsiveMode, onTogglePages, onToggleSettings }) {
  const [calloutOpen, setCalloutOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [hiddenGroups, setHiddenGroups] = useState(new Set());
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 600px)').matches);
  const toolbarRef = useRef(null);
  const overflowRef = useRef(false);
  const mobileRef = useRef(isMobile);
  const measureRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => { setIsMobile(e.matches); mobileRef.current = e.matches; };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (preview) return;
    const el = toolbarRef.current;
    if (!el) return;
    let rafId = 0;
    const measure = () => {
      if (!el.isConnected) return;
      if (overflowRef.current && mobileRef.current) return;
      const groups = el.querySelectorAll('[data-overflow-group]');
      const moreBtn = el.querySelector('.tt-overflow-trigger');
      if (!groups.length) return;

      groups.forEach(g => g.classList.remove('tt-overflow-hidden'));
      if (moreBtn) moreBtn.style.display = 'none';

      const barRight = el.getBoundingClientRect().right;
      const pad = 40;
      let count = 0;
      const hidden = new Set();
      for (let i = groups.length - 1; i >= 0; i--) {
        const g = groups[i];
        if (g.dataset.overflowGroup === 'keep') continue;
        if (g.getBoundingClientRect().right > barRight - pad) {
          g.classList.add('tt-overflow-hidden');
          hidden.add(g.dataset.overflowGroup);
          count++;
        }
      }
      if (count > 0 && moreBtn) moreBtn.style.display = '';
      if (count === 0) setOverflowOpen(false);
      setHiddenCount(count);
      setHiddenGroups(prev => {
        if (prev.size === hidden.size && [...hidden].every(g => prev.has(g))) return prev;
        return hidden;
      });
    };
    measureRef.current = measure;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    });
    ro.observe(el);
    ro.observe(el.closest('.workspace-wrap') || el.parentElement);
    return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
  }, [editor, preview]);

  useEffect(() => {
    overflowRef.current = overflowOpen;
    const el = toolbarRef.current;
    if (!el) return;
    if (overflowOpen && isMobile) {
      el.querySelectorAll('.tt-overflow-hidden').forEach(g => g.classList.remove('tt-overflow-hidden'));
    } else if (!overflowOpen && measureRef.current) {
      measureRef.current();
    }
  }, [overflowOpen, isMobile]);

  if (preview) return null;

  if (!editor) return null;

  const run = (fn) => {
    const el = editor.view.dom.closest('.page-content');
    const top = el ? el.scrollTop : 0;
    fn(editor.chain().focus()).run();
    if (el) requestAnimationFrame(() => { el.scrollTop = top; });
  };

  const activeCallout = CALLOUTS.find(c => editor.isActive('blockquote', { tone: c.tone }));

  const toggleCallout = (tone) => {
    if (editor.isActive('blockquote', { tone })) run(c => c.toggleBlockquote());
    else if (editor.isActive('blockquote')) run(c => c.updateAttributes('blockquote', { tone }));
    else run(c => c.toggleBlockquote().updateAttributes('blockquote', { tone }));
    setCalloutOpen(false);
  };

  const blockType = editor.isActive('heading', { level: 1 }) ? 'h1'
    : editor.isActive('heading', { level: 2 }) ? 'h2'
    : editor.isActive('heading', { level: 3 }) ? 'h3' : 'p';

  const setBlockType = (val) => {
    if (val === 'p') run(c => c.setParagraph());
    else if (val === 'h1') run(c => c.toggleHeading({ level: 1 }));
    else if (val === 'h2') run(c => c.toggleHeading({ level: 2 }));
    else if (val === 'h3') run(c => c.toggleHeading({ level: 3 }));
    setStyleOpen(false);
  };

  const BLOCK_LABELS = { p: 'Normal text', h1: 'Heading 1', h2: 'Heading 2', h3: 'Heading 3' };

  return (
    <div className={`tt-toolbar${overflowOpen && isMobile ? ' tt-toolbar--expanded' : ''}`} ref={toolbarRef}>
      {responsiveMode && (
        <div data-overflow-group="keep" className="tt-overflow-group">
          <button className="tt-btn" onClick={onTogglePages} data-tip="Pages" aria-label="Pages">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M5.5 2.5v11" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
          </button>
          <button className="tt-btn" onClick={onToggleSettings} data-tip="Settings" aria-label="Settings">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M6.5 2h3l.4 1.6a4.5 4.5 0 011.1.6l1.5-.5 1.5 2.6-1.2 1a4.5 4.5 0 010 1.3l1.2 1-1.5 2.6-1.5-.5a4.5 4.5 0 01-1.1.6L9.5 14h-3l-.4-1.6a4.5 4.5 0 01-1.1-.6l-1.5.5-1.5-2.6 1.2-1a4.5 4.5 0 010-1.3l-1.2-1 1.5-2.6 1.5.5a4.5 4.5 0 011.1-.6L6.5 2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
              <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
          </button>
        </div>
      )}
      {responsiveMode && <span className="tt-div-v" />}
      <div data-overflow-group="keep" className="tt-overflow-group">
        <button className="tt-btn"
                onClick={onUndo}
                disabled={!canUndo} data-tip="Undo" aria-label="Undo">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 6.5h8a3 3 0 0 1 0 6H8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M5 3.5L2 6.5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <button className="tt-btn"
                onClick={onRedo}
                disabled={!canRedo} data-tip="Redo" aria-label="Redo">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M14 6.5H6a3 3 0 0 0 0 6h1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M11 3.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>

      {onSetZoom && (
        <div data-overflow-group="keep" className="tt-overflow-group">
          <div className="inline-select tt-zoom-dropdown">
            <button type="button" className="inline-select__trigger" onClick={() => setZoomOpen(o => !o)}>
              {zoom === 'fit' ? 'Fit' : `${zoom || 100}%`}
              <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            </button>
            {zoomOpen && (
              <>
                <div className="inline-select__backdrop" onClick={() => setZoomOpen(false)} />
                <div className="menu inline-select__menu">
                  <button className={`menu__item${zoom === 'fit' ? ' menu__item--selected' : ''}`}
                    onClick={() => { onSetZoom('fit'); setZoomOpen(false); }}>Fit</button>
                  <div className="menu__separator" />
                  {[50, 75, 90, 100, 125, 150, 200].map(v => (
                    <button key={v}
                      className={`menu__item${zoom === v ? ' menu__item--selected' : ''}`}
                      onClick={() => { onSetZoom(v); setZoomOpen(false); }}>{v}%</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <span className="tt-div-v" />

      <div data-overflow-group="keep" className="tt-overflow-group">
        <div className="inline-select tt-style-dropdown">
          <button type="button" className="inline-select__trigger" onClick={() => setStyleOpen(o => !o)}>
            {BLOCK_LABELS[blockType]}
            <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
          </button>
          {styleOpen && (
            <>
              <div className="inline-select__backdrop" onClick={() => setStyleOpen(false)} />
              <div className="menu inline-select__menu">
                <button className={`menu__item tt-style--p${blockType === 'p' ? ' menu__item--selected' : ''}`} onClick={() => setBlockType('p')}>Normal text</button>
                <button className={`menu__item tt-style--h1${blockType === 'h1' ? ' menu__item--selected' : ''}`} onClick={() => setBlockType('h1')}>Heading 1</button>
                <button className={`menu__item tt-style--h2${blockType === 'h2' ? ' menu__item--selected' : ''}`} onClick={() => setBlockType('h2')}>Heading 2</button>
                <button className={`menu__item tt-style--h3${blockType === 'h3' ? ' menu__item--selected' : ''}`} onClick={() => setBlockType('h3')}>Heading 3</button>
              </div>
            </>
          )}
        </div>

        <div className="inline-select tt-size-dropdown">
          <button type="button" className="inline-select__trigger" onClick={() => setSizeOpen(o => !o)} aria-label="Text size">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 3.5V2h6v1.5M4 2v12M4 14H2.5M5.5 14H4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 8.5V7h5v1.5M11.5 7v7M11.5 14H10M13 14h-1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
          </button>
          {sizeOpen && (
            <>
              <div className="inline-select__backdrop" onClick={() => setSizeOpen(false)} />
              <div className="menu inline-select__menu">
                {FONT_SIZES.map(s => (
                  <button
                    key={s.label}
                    className={`menu__item tt-size--${s.label.toLowerCase()}${editor.getAttributes('textStyle').fontSize === s.value ? ' menu__item--selected' : ''}`}
                    onClick={() => { run(c => c.setFontSize(s.value)); setSizeOpen(false); }}
                  >{s.label}</button>
                ))}
                <div className="menu__separator" />
                <button
                  className="menu__item"
                  onClick={() => { run(c => c.unsetFontSize()); setSizeOpen(false); }}
                >Reset</button>
              </div>
            </>
          )}
        </div>
      </div>

      <span className="tt-div-v" />

      <div data-overflow-group="format" className="tt-overflow-group">
        <button
          type="button"
          className={`tt-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
          onClick={() => run(c => c.toggleBold())}
          data-tip="Bold" aria-label="Bold"
        ><b>B</b></button>
        <button
          type="button"
          className={`tt-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
          onClick={() => run(c => c.toggleItalic())}
          data-tip="Italic" aria-label="Italic"
        ><i>I</i></button>
        <button
          type="button"
          className={`tt-btn ${editor.isActive('underline') ? 'is-active' : ''}`}
          onClick={() => run(c => c.toggleUnderline())}
          data-tip="Underline" aria-label="Underline"
        ><u>U</u></button>
      </div>

      <span className="tt-div-v" />

      <button
        type="button"
        data-overflow-group="bulletList"
        className={`tt-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
        onClick={() => run(c => c.toggleBulletList())}
        data-tip="Bullet list" aria-label="Bullet list"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="3" cy="4" r="1.2" fill="currentColor"/><circle cx="3" cy="8" r="1.2" fill="currentColor"/><circle cx="3" cy="12" r="1.2" fill="currentColor"/><path d="M6 4h8M6 8h8M6 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
      </button>
      <button
        type="button"
        data-overflow-group="orderedList"
        className={`tt-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
        onClick={() => run(c => c.toggleOrderedList())}
        data-tip="Numbered list" aria-label="Numbered list"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><text x="1.5" y="5.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">1</text><text x="1.5" y="9.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">2</text><text x="1.5" y="13.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">3</text><path d="M6 4h8M6 8h8M6 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
      </button>
      <button
        type="button"
        data-overflow-group="taskList"
        className={`tt-btn ${editor.isActive('taskList') ? 'is-active' : ''}`}
        onClick={() => run(c => c.toggleTaskList())}
        data-tip="Checklist" aria-label="Checklist"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.4"/><rect x="2" y="9" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 5h6M8 11h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
      </button>
      <button
        type="button"
        data-overflow-group="valueRow"
        className={`tt-btn ${editor.isActive('valueRow') ? 'is-active' : ''}`}
        onClick={() => run(c => c.toggleValueRow())}
        data-tip="Value row" aria-label="Value row"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 5h4M10 5h4M2 11h3M10 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M8 3v10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeDasharray="1.2 1.8"/></svg>
      </button>

      <button type="button" data-overflow-group="indent" className="tt-btn" onClick={() => run(c => c.indent())} data-tip="Indent" aria-label="Indent">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M7 7h7M7 11h7M2 15h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M2 7l3 2-3 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <button type="button" data-overflow-group="outdent" className="tt-btn" onClick={() => run(c => c.outdent())} data-tip="Outdent" aria-label="Outdent">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M7 7h7M7 11h7M2 15h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M5 7l-3 2 3 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>

      <span className="tt-div-v" />

      <button
        type="button"
        data-overflow-group="divider"
        className="tt-btn"
        onClick={() => run(c => c.setHorizontalRule())}
        data-tip="Divider" aria-label="Divider"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 8h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
      </button>
      <button
        type="button"
        data-overflow-group="image"
        className="tt-btn"
        onClick={() => insertImage(editor)}
        data-tip="Image" aria-label="Insert image"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/><circle cx="5" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1.5 11l3.5-3.5L8 10.5l2.5-2.5 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <button
        type="button"
        data-overflow-group="pageButton"
        className="tt-btn"
        onClick={() => run(c => c.insertPageButton())}
        data-tip="Page button" aria-label="Insert page button"
      >
        <Ico.PageButton width="14" height="14"/>
      </button>
      <div data-overflow-group="table" className="tt-table-dropdown">
          <button
            type="button"
            className={`tt-btn${editor.isActive('table') ? ' is-active' : ''}`}
            onClick={() => {
              if (editor.isActive('table')) {
                setTableMenuOpen(o => !o);
              } else {
                run(c => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }));
              }
            }}
            data-tip="Table" aria-label="Insert table"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/><path d="M1.5 6.5h13M1.5 10h13M6 2.5v11M10.5 2.5v11" stroke="currentColor" strokeWidth="1.2"/></svg>
            {editor.isActive('table') && <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>}
          </button>
          {tableMenuOpen && editor.isActive('table') && (
            <>
              <div className="tt-table-backdrop" onClick={() => setTableMenuOpen(false)} />
              <div className="menu tt-table-menu">
                <button className="menu__item" onClick={() => { run(c => c.addColumnBefore()); setTableMenuOpen(false); }}>
                  Add column before
                </button>
                <button className="menu__item" onClick={() => { run(c => c.addColumnAfter()); setTableMenuOpen(false); }}>
                  Add column after
                </button>
                <button className="menu__item" onClick={() => { run(c => c.addRowBefore()); setTableMenuOpen(false); }}>
                  Add row before
                </button>
                <button className="menu__item" onClick={() => { run(c => c.addRowAfter()); setTableMenuOpen(false); }}>
                  Add row after
                </button>
                <div className="menu__separator" />
                <button className="menu__item" onClick={() => { run(c => c.deleteColumn()); setTableMenuOpen(false); }}>
                  Delete column
                </button>
                <button className="menu__item" onClick={() => { run(c => c.deleteRow()); setTableMenuOpen(false); }}>
                  Delete row
                </button>
                <div className="menu__separator" />
                <button className="menu__item" onClick={() => { run(c => c.toggleHeaderRow()); setTableMenuOpen(false); }}>
                  Toggle header row
                </button>
                <div className="menu__separator" />
                <button className="menu__item menu__item--danger" onClick={() => { run(c => c.deleteTable()); setTableMenuOpen(false); }}>
                  Delete table
                </button>
              </div>
            </>
          )}
        </div>

      <div data-overflow-group="callout" className="tt-callout-dropdown">
        <button
          type="button"
          className={`tt-btn${activeCallout ? ' is-active' : ''}`}
          onClick={() => setCalloutOpen(o => !o)}
          data-tip="Callout" aria-label="Callout"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 2v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M7 5h7M7 8h5M7 11h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
          <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
        </button>
        {calloutOpen && (
          <>
            <div className="tt-callout-backdrop" onClick={() => setCalloutOpen(false)} />
            <div className="menu tt-callout-menu">
              {CALLOUTS.map(c => (
                <button
                  key={c.tone}
                  className={`menu__item ${c.cls}${activeCallout?.tone === c.tone ? ' menu__item--active' : ''}`}
                  onClick={() => toggleCallout(c.tone)}
                >
                  {c.icon}
                  {c.label}
                </button>
              ))}
              {activeCallout && (
                <>
                  <div className="menu__separator" />
                  <button
                    className="menu__item"
                    onClick={() => { run(c => c.toggleBlockquote()); setCalloutOpen(false); }}
                  >Remove callout</button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Overflow trigger */}
      <div className="tt-overflow-trigger" style={{display: hiddenCount > 0 || overflowOpen ? '' : 'none'}}>
        <button type="button" className={`tt-btn${overflowOpen && !isMobile ? ' is-active' : ''}`}
                onClick={() => setOverflowOpen(o => !o)} data-tip={overflowOpen && isMobile ? 'Less' : 'More'} aria-label={overflowOpen && isMobile ? 'Show less' : 'More actions'}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="3" cy="8" r="1.3" fill="currentColor"/><circle cx="8" cy="8" r="1.3" fill="currentColor"/><circle cx="13" cy="8" r="1.3" fill="currentColor"/></svg>
        </button>
        {overflowOpen && !isMobile && (
          <>
            <div className="tt-overflow-backdrop" onClick={() => setOverflowOpen(false)} />
            <div className="menu tt-overflow-menu">
              {hiddenGroups.has('format') && <>
                <button className={`menu__item${editor.isActive('bold')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleBold());setOverflowOpen(false);}}>
                  <b>B</b> Bold
                </button>
                <button className={`menu__item${editor.isActive('italic')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleItalic());setOverflowOpen(false);}}>
                  <i>I</i> Italic
                </button>
                <button className={`menu__item${editor.isActive('underline')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleUnderline());setOverflowOpen(false);}}>
                  <u>U</u> Underline
                </button>
              </>}
              {hiddenGroups.has('bulletList') && (
                <button className={`menu__item${editor.isActive('bulletList')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleBulletList());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="3" cy="4" r="1.2" fill="currentColor"/><circle cx="3" cy="8" r="1.2" fill="currentColor"/><circle cx="3" cy="12" r="1.2" fill="currentColor"/><path d="M6 4h8M6 8h8M6 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Bullet list
                </button>
              )}
              {hiddenGroups.has('orderedList') && (
                <button className={`menu__item${editor.isActive('orderedList')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleOrderedList());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><text x="1.5" y="5.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">1</text><text x="1.5" y="9.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">2</text><text x="1.5" y="13.5" fill="currentColor" fontSize="5" fontFamily="var(--font-sans)" fontWeight="600">3</text><path d="M6 4h8M6 8h8M6 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Numbered list
                </button>
              )}
              {hiddenGroups.has('taskList') && (
                <button className={`menu__item${editor.isActive('taskList')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleTaskList());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.4"/><rect x="2" y="9" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 5h6M8 11h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Checklist
                </button>
              )}
              {hiddenGroups.has('valueRow') && (
                <button className={`menu__item${editor.isActive('valueRow')?' menu__item--active':''}`}
                        onClick={()=>{run(c=>c.toggleValueRow());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 5h4M10 5h4M2 11h3M10 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M8 3v10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeDasharray="1.2 1.8"/></svg>
                  Value row
                </button>
              )}
              {hiddenGroups.has('indent') && (
                <button className="menu__item"
                        onClick={()=>{run(c=>c.indent());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M7 7h7M7 11h7M2 15h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M2 7l3 2-3 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Indent
                </button>
              )}
              {hiddenGroups.has('outdent') && (
                <button className="menu__item"
                        onClick={()=>{run(c=>c.outdent());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M7 7h7M7 11h7M2 15h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M5 7l-3 2 3 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Outdent
                </button>
              )}
              {hiddenGroups.has('divider') && (
                <button className="menu__item"
                        onClick={()=>{run(c=>c.setHorizontalRule());setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 8h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Divider
                </button>
              )}
              {hiddenGroups.has('image') && (
                <button className="menu__item"
                        onClick={()=>{insertImage(editor);setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/><circle cx="5" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1.5 11l3.5-3.5L8 10.5l2.5-2.5 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Image
                </button>
              )}
              {hiddenGroups.has('pageButton') && (
                <button className="menu__item"
                        onClick={()=>{run(c=>c.insertPageButton());setOverflowOpen(false);}}>
                  <Ico.PageButton width="14" height="14"/>
                  Page button
                </button>
              )}
              {hiddenGroups.has('table') && (
                <button className="menu__item"
                        onClick={()=>{run(c=>c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }));setOverflowOpen(false);}}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/><path d="M1.5 6.5h13M1.5 10h13M6 2.5v11M10.5 2.5v11" stroke="currentColor" strokeWidth="1.2"/></svg>
                  Table
                </button>
              )}
              {hiddenGroups.has('callout') && CALLOUTS.map(c => (
                <button key={c.tone} className={`menu__item ${c.cls}${editor.isActive('blockquote', {tone: c.tone})?' menu__item--active':''}`}
                        onClick={()=>{toggleCallout(c.tone);setOverflowOpen(false);}}>
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

    </div>
  );
}

export function TiptapEditor({ content, onChange, onEditorReady, editable = true,
                               doc, documentId, onInsertInlineTab, onOpenPage, onRenameInlineTab,
                               onUpdateInlineTab, onDeleteInlineTab, onClonePageButtonSubtree,
                               onRestoreInlineSubtree }) {
  const contentKey = useRef(JSON.stringify(content || EMPTY_DOC));
  const suppressUpdate = useRef(false);
  const editorRef = useRef(null);

  const handleUpdate = useCallback(({ editor }) => {
    if (suppressUpdate.current) return;
    const doc = editor.isEmpty ? null : editor.getJSON();
    contentKey.current = JSON.stringify(doc);
    if (onChange) onChange(doc);
  }, [onChange]);

  const initialDoc = useMemo(() => content || EMPTY_DOC, []);

  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;

  const handleFocus = useCallback(({ editor }) => {
    if (onEditorReadyRef.current) onEditorReadyRef.current(editor);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        blockquote: false,
        history: false,
      }),
      CalloutBlockquote,
      Placeholder.configure({
        includeChildren: true,
        showOnlyCurrent: false,
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') {
            return ['', 'Heading', 'Subheading', 'Small heading'][node.attrs.level] || 'Heading';
          }
          if (node.type.name === 'valueRowLabel') return 'Label';
          if (node.type.name === 'valueRowValue') return 'Value';
          return '';
        },
      }),
      Underline,
      FontSize,
      TaskList,
      TaskItem.configure({ nested: false }),
      ResizableImage,
      ValueRow,
      ValueRowLabel,
      ValueRowValue,
      Indent,
      PageButton,
      Table,
      TableRow,
      TableHeader,
      TableCell,
    ],
    editable,
    content: initialDoc,
    onCreate: ({ editor }) => {
      editorRef.current = editor;
      if (onEditorReadyRef.current) onEditorReadyRef.current(editor);
    },
    onUpdate: handleUpdate,
    onFocus: handleFocus,
    editorProps: {
      attributes: {
        class: 'tt-canvas',
      },
      handleKeyDown: (_view, event) => {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
          event.preventDefault();
          return true;
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
          event.preventDefault();
          return true;
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const file = files[0];
        if (!file.type.startsWith('image/')) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
        setTimeout(() => uploadAndInsert(editorRef.current, file, pos?.pos), 0);
        return true;
      },
      handleDOMEvents: {
        // For each pageButton in the cut selection: snapshot its inline
        // subtree (tab + pages + nested inline tabs) into the cutBuffer,
        // then delete it from the doc so the page disappears immediately.
        // A follow-up paste will look up the snapshot and restore the same
        // tabId (move semantics). ProseMirror still performs its default
        // cut after we return false (clipboard write + selection delete).
        cut: (view) => {
          const { state } = view;
          const sel = state.selection;
          if (sel.empty) return false;
          const storage = editorRef.current?.storage?.pageButton;
          const onDelete = storage?.onDeleteInlineTab;
          const docSnap = storage?.doc;
          const docId = storage?.documentId;
          if (!onDelete || !docSnap) {
            // Block PM's default cut: without the hand-off hooks the inline
            // tab+pages stay in the doc as orphans while the editor node is
            // removed. Better to no-op the cut than to silently lose content.
            console.error('cut: pageButton storage hooks not ready, blocking default cut');
            return true;
          }
          const now = Date.now();
          for (const [k, v] of cutBuffer) {
            if (v.expiry <= now) cutBuffer.delete(k);
          }
          const expiry = now + CUT_BUFFER_TTL_MS;
          state.doc.nodesBetween(sel.from, sel.to, (node) => {
            if (node.type.name === 'pageButton' && node.attrs.tabId) {
              const tabId = node.attrs.tabId;
              const snap = snapshotInlineSubtree(docSnap, tabId);
              if (snap && snap.tabs.length > 0) {
                cutBuffer.set(tabId, { ...snap, expiry, docId });
                onDelete(tabId, { silent: true });
              } else {
                // docSnap stale vs PM state (mid-undo/redo, race). Skip
                // the explicit delete; the orphan sweeper will reconcile
                // once state settles rather than committing a delete with
                // nothing in the buffer to restore.
                console.error('cut: snapshot empty for', tabId, '— skipping delete');
              }
            }
          });
          return false;
        },
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) return false;
            setTimeout(() => uploadAndInsert(editorRef.current, file), 0);
            return true;
          }
        }
        return false;
      },
      transformPasted: (slice) => {
        const clone = editor?.storage?.pageButton?.onClonePageButtonSubtree;
        const restore = editor?.storage?.pageButton?.onRestoreInlineSubtree;
        const currentDocId = editor?.storage?.pageButton?.documentId;
        const now = Date.now();
        let droppedButtons = false;
        const rewriteNode = (node) => {
          if (node.type.name === 'pageButton') {
            const oldTabId = node.attrs.tabId;
            // If this tabId was just cut from THIS document, restore the
            // snapshot (tab + pages + nested inline subtree) and reuse the
            // original tabId — move semantics, no clone, no orphan. Match
            // on docId so a cut in another doc can't graft foreign tabs.
            const entry = cutBuffer.get(oldTabId);
            if (entry && entry.docId === currentDocId && entry.expiry > now) {
              if (!restore) {
                // Hook race (HMR). Keep the buffer entry so a retry can
                // succeed once restore is wired; falling into clone would
                // hit "root tab not found" since cut already removed it.
                console.error('pageButton paste: restore hook not ready, cutBuffer entry kept for retry');
                droppedButtons = true;
                return null;
              }
              cutBuffer.delete(oldTabId);
              restore({ tabs: entry.tabs, pages: entry.pages });
              return node.type.create(node.attrs, node.content, node.marks);
            }
            if (!clone) {
              console.error('pageButton paste dropped: clone hook not ready');
              droppedButtons = true;
              return null;
            }
            const newTabId = clone(oldTabId);
            if (!newTabId) { droppedButtons = true; return null; }
            return node.type.create({ ...node.attrs, tabId: newTabId }, node.content, node.marks);
          }
          if (node.content && node.content.size > 0) {
            const newChildren = [];
            node.content.forEach(child => {
              const replaced = rewriteNode(child);
              if (replaced) newChildren.push(replaced);
            });
            return node.type.create(node.attrs, Fragment.fromArray(newChildren), node.marks);
          }
          return node;
        };
        const newContent = [];
        slice.content.forEach(child => {
          const replaced = rewriteNode(child);
          if (replaced) newContent.push(replaced);
        });
        if (droppedButtons) showToast('Some page buttons could not be pasted', { type: 'warning' });
        return new Slice(Fragment.fromArray(newContent), slice.openStart, slice.openEnd);
      },
    },
  });

  useLayoutEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable, false);
  }, [editable, editor]);

  useEffect(() => {
    if (!editor) return;
    const doc = content || EMPTY_DOC;
    const newKey = JSON.stringify(doc);
    if (newKey !== contentKey.current) {
      contentKey.current = newKey;
      suppressUpdate.current = true;
      editor.commands.setContent(doc);
      suppressUpdate.current = false;
    }
  }, [content, editor]);

  useLayoutEffect(() => {
    if (!editor) return;
    editor.storage.pageButton.doc = doc;
    editor.storage.pageButton.documentId = documentId;
    editor.storage.pageButton.onOpenPage = onOpenPage;
    editor.storage.pageButton.onInsertInlineTab = onInsertInlineTab;
    editor.storage.pageButton.onRenameInlineTab = onRenameInlineTab;
    editor.storage.pageButton.onUpdateInlineTab = onUpdateInlineTab;
    editor.storage.pageButton.onDeleteInlineTab = onDeleteInlineTab;
    editor.storage.pageButton.onClonePageButtonSubtree = onClonePageButtonSubtree;
    editor.storage.pageButton.onRestoreInlineSubtree = onRestoreInlineSubtree;
  }, [editor, doc, documentId, onOpenPage, onInsertInlineTab, onRenameInlineTab, onUpdateInlineTab, onDeleteInlineTab, onClonePageButtonSubtree, onRestoreInlineSubtree]);

  const pageButtonContextValue = useMemo(() => ({
    editable, doc, onOpenPage, onInsertInlineTab, onRenameInlineTab,
    onUpdateInlineTab, onDeleteInlineTab, onClonePageButtonSubtree, onRestoreInlineSubtree,
  }), [editable, doc, onOpenPage, onInsertInlineTab, onRenameInlineTab, onUpdateInlineTab, onDeleteInlineTab, onClonePageButtonSubtree, onRestoreInlineSubtree]);

  if (!editor) return null;

  return (
    <PageButtonContext.Provider value={pageButtonContextValue}>
      <EditorContent editor={editor} />
    </PageButtonContext.Provider>
  );
}
